import { useRef, useState } from "react";
import { ImagePlus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const MAX_MB = 8;
const OK_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ImagePicker({ value, fileName, onChange, testId = "image-picker", label = "Select Image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const process = (file) => {
    if (!file) return; // user cancelled the picker
    if (!OK_TYPES.includes(file.type)) {
      toast.error("Please select a JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error("Your image is too large. Please choose a smaller image.");
      return;
    }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1000 / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange(canvas.toDataURL("image/jpeg", 0.85), file.name);
        setBusy(false);
      };
      img.onerror = () => {
        toast.error("That file doesn't look like a valid image. Please try another one.");
        setBusy(false);
      };
      img.src = reader.result;
    };
    reader.onerror = () => {
      toast.error("We couldn't read that file. Please try another image.");
      setBusy(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => {
          process(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={`${testId}-input`}
      />
      {!value ? (
        <button type="button" onClick={openPicker} disabled={busy} className="w-full min-h-[96px] rounded-xl border-2 border-dashed border-[#E0A96D]/70 bg-white hover:border-[#B45309] hover:bg-[#F9F3EA] transition-colors flex flex-col items-center justify-center gap-1.5 text-stone-500 disabled:opacity-60" data-testid={`${testId}-select`}>
          <ImagePlus size={20} className="text-[#B45309]" />
          <span className="text-sm font-semibold">{busy ? "Processing image…" : label}</span>
          <span className="text-[11px] text-stone-400">JPG, PNG or WEBP · up to {MAX_MB}MB</span>
        </button>
      ) : (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-[#E0A96D]/50 bg-white" data-testid={`${testId}-preview-wrap`}>
          <img src={value} alt="Selected personalization" className="w-16 h-16 rounded-lg object-cover border border-stone-200" data-testid={`${testId}-preview`} />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-stone-700 truncate" data-testid={`${testId}-filename`}>{fileName || "Selected image"}</p>
            <p className="text-[11px] text-stone-400">This photo will be printed on your gift.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <button type="button" onClick={openPicker} className="flex items-center gap-1 px-3 h-8 rounded-full border border-stone-200 text-[11px] font-bold text-stone-600 hover:border-[#B45309] hover:text-[#B45309] transition-colors" data-testid={`${testId}-change`}>
              <RefreshCw size={11} /> Change Image
            </button>
            <button type="button" onClick={() => onChange(null, null)} className="flex items-center gap-1 px-3 h-8 rounded-full border border-stone-200 text-[11px] font-bold text-stone-600 hover:border-red-300 hover:text-red-600 transition-colors" data-testid={`${testId}-remove`}>
              <Trash2 size={11} /> Remove Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
